'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Cauthmodal } from '@/components/ui/Cauthmodal';
import { Cmodal } from '@/components/ui/Cmodal';
import { Cportal } from '@/components/ui/Cportal';
import { ModalLocation } from '@/constants/componentPresets';
import { useAuth } from '@/contexts/auth';
import { zIndex } from '@/theme/zIndex';
import { AuthModalState } from '@/types/auth-types';
import {
  AuthModalContext,
  type AuthModalContextType,
  type OpenAuthModalOptions,
} from './auth-modal-context';

export { useAuthModal } from './auth-modal-context';
export type { OpenAuthModalOptions } from './auth-modal-context';

/**
 * The single app-wide auth modal. Presentation is the landing-page shape —
 * `Cmodal` at `ModalLocation.Center` over a `Cblurbackdrop`, with the tile
 * background off so the auth panel sits directly on the blur. Every surface
 * that needs sign-in opens THIS instance through `useAuthModal`; none render
 * their own.
 */
const AUTH_MODAL_PADDING = '20px 30px';

/**
 * Own portal container, so the gate gets its own stacking context. `Cmodal`
 * positions its backdrop at `zIndex.modalBackdrop` (50) and its container at
 * `zIndex.modalContainer` (300) — both far below the teams-gallery deck (900),
 * its expanded team overlay (1000), and above-modal overlays (10000), any of
 * which can raise the gate. Containing those layers in a context at
 * `zIndex.authModal` lifts the whole modal above them without `Cmodal` needing
 * to know about z at all. The container is zero-size and only its `fixed`
 * children paint, so it never intercepts pointer events while closed.
 */
const AUTH_MODAL_PORTAL_CONTAINER_ID = 'auth-modal-portal';

export function AuthModalProvider({ children }: { children: ReactNode }): ReactNode {
  const [visible, setVisible] = useState(false);
  const [modalInitialState, setModalInitialState] = useState<AuthModalState>(
    AuthModalState.Initial,
  );
  const onDismissRef = useRef<(() => void) | undefined>(undefined);
  const auth = useAuth();
  const isAuthenticated = Boolean(auth.data?.user);

  const openAuthModal = useCallback((options?: OpenAuthModalOptions): void => {
    setModalInitialState(options?.initialState ?? AuthModalState.Initial);
    onDismissRef.current = options?.onDismiss;
    setVisible(true);
  }, []);

  const closeAuthModal = useCallback((): void => {
    setVisible(false);
    const onDismiss = onDismissRef.current;
    onDismissRef.current = undefined;
    onDismiss?.();
  }, []);

  // A sign-in gate has served its purpose the moment the session lands, so it
  // dismisses itself rather than making every caller repeat that effect.
  // Confirmation states (ActivationComplete) are NOT gates — /auth/callback
  // opens one precisely because the user just authenticated, so auto-dismiss
  // must not reach it.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (modalInitialState !== AuthModalState.Initial) return;
    onDismissRef.current = undefined;
    setVisible(false);
  }, [isAuthenticated, modalInitialState]);

  const contextValue = useMemo<AuthModalContextType>(
    () => ({
      openAuthModal,
      closeAuthModal,
      isAuthModalOpen: visible,
    }),
    [openAuthModal, closeAuthModal, visible],
  );

  return (
    <AuthModalContext.Provider value={contextValue}>
      {children}
      <Cportal
        containerId={AUTH_MODAL_PORTAL_CONTAINER_ID}
        containerZIndex={zIndex.authModal}
      >
        <Cmodal
          dataElScope="AuthModalProvider/Modal"
          visible={visible}
          location={ModalLocation.Center}
          useContainerBackground={false}
          onClose={closeAuthModal}
          content={
            <Cauthmodal
              initialState={modalInitialState}
              onClose={closeAuthModal}
              padding={AUTH_MODAL_PADDING}
            />
          }
        />
      </Cportal>
    </AuthModalContext.Provider>
  );
}
